import React, { useState } from 'react';
import { Box, Typography, Grid, Link, Paper } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Form, Question } from '../../api/types/forms';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  processAnswers,
  separateAnswers,
  groupForEachAnswers,
  findSourceQuestionId,
  getForEachContextLabel,
  findQuestion,
  getSourceQuestionsToHide,
  filterRegularQuestions,
} from './formAnswerUtils';

// Local type for processed answers as key-value pairs
type ProcessedFormAnswers = Record<string, any>;

// We assume these types exist based on other components.
// If not, we might need to define them more simply.

interface FormAnswersViewerProps {
  formTemplateJson?: string;
  formAnswersJson?: string;
  // New props for forms API
  form?: any;
  answers?: any[];
}

// --- URL Type Guards ---
const isUrlWithExtension = (url: any, extensions: RegExp): url is string => {
  if (typeof url !== 'string') return false;
  try {
    // Check if it's a valid URL format first
    const urlObject = new URL(url);
    // Now check the extension in the pathname
    return extensions.test(urlObject.pathname);
  } catch (_) {
    // Not a valid URL string
    return false;
  }
};

// Check for malformed MIME-type-as-extension pattern (e.g., ".image/jpeg", ".video/mp4")
const hasMimeTypeExtension = (url: any, mimePrefix: string): boolean => {
  if (typeof url !== 'string') return false;
  try {
    const urlObject = new URL(url);
    // Check for patterns like ".image/jpeg" or ".video/mp4" in the pathname
    return urlObject.pathname.includes(`.${mimePrefix}/`);
  } catch (_) {
    return false;
  }
};

const isImageUrl = (url: any): url is string => 
  isUrlWithExtension(url, /\.(jpeg|jpg|gif|png|svg|webp|bmp|tiff)$/i) ||
  hasMimeTypeExtension(url, 'image');

const isVideoUrl = (url: any): url is string =>
  isUrlWithExtension(url, /\.(mp4|webm|mov|avi|mkv)$/i) ||
  hasMimeTypeExtension(url, 'video');

const isAudioUrl = (url: any): url is string =>
  isUrlWithExtension(url, /\.(mp3|wav|ogg|aac|flac)$/i) ||
  hasMimeTypeExtension(url, 'audio');

const isPdfUrl = (url: any): url is string =>
  isUrlWithExtension(url, /\.pdf$/i) ||
  url.includes('.application/pdf');

const RenderUrlFallback = ({ url, questionText }: { url: string; questionText?: string }) => {
  const [currentPreviewType, setCurrentPreviewType] = useState<'image' | 'video' | 'audio' | 'pdf' | 'link'>('image');

  const handlePreviewError = () => {
    if (currentPreviewType === 'image') {
      setCurrentPreviewType('video');
    } else if (currentPreviewType === 'video') {
      setCurrentPreviewType('audio');
    } else if (currentPreviewType === 'audio') {
      setCurrentPreviewType('pdf');
    } else if (currentPreviewType === 'pdf') {
      setCurrentPreviewType('link');
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef'
      }}
    >
      {currentPreviewType === 'image' && (
        <img
          src={url}
          alt={questionText || 'preview'}
          style={{
            width: '100%',
            maxHeight: 500,
            borderRadius: 8,
            objectFit: 'contain',
            display: 'block',
            backgroundColor: '#fff'
          }}
          onError={handlePreviewError}
        />
      )}

      {currentPreviewType === 'video' && (
        <video
          src={url}
          controls
          style={{
            width: '100%',
            maxHeight: 500,
            borderRadius: 8,
            display: 'block',
            objectFit: 'contain'
          }}
          onError={handlePreviewError}
        />
      )}

      {currentPreviewType === 'audio' && (
        <audio
          src={url}
          controls
          style={{
            width: '100%',
            display: 'block'
          }}
          onError={handlePreviewError}
        />
      )}

      {currentPreviewType === 'pdf' && (
        <iframe
          src={`${url}#pagemode=thumbs&navpanes=1`}
          style={{
            width: '100%',
            maxWidth: 700,
            height: '650px',
            border: 'none',
            borderRadius: 8,
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
          title={questionText ? `PDF preview for ${questionText}` : 'PDF preview'}
          onError={handlePreviewError}
        />
      )}

      {/* Always show the link, but make it more prominent if no preview worked */}
      <Box mt={2} display="flex" justifyContent="center">
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            backgroundColor: '#1976d2',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#1565c0',
            }
          }}
        >
          <OpenInNewIcon fontSize="small" />
          <Typography variant="body2">Abrir vista completa</Typography>
        </Link>
      </Box>
    </Paper>
  );
};

const isLikelyUrl = (text: any): text is string => {
  if (typeof text !== 'string') return false;
  return text.startsWith('http://') || text.startsWith('https://');
};

const RenderAnswerContent = ({ ans, questionText, isMultiselectOption }: { ans: any; questionText?: string; isMultiselectOption?: boolean }) => {
  if (isLikelyUrl(ans)) {
    return <RenderUrlFallback url={ans} questionText={questionText} />;
  }
  // For multiselect options, display as clean text without brackets
  if (isMultiselectOption) {
    return <Typography variant="body1" color="textPrimary" sx={{ whiteSpace: 'pre-wrap', fontWeight: 600 }}>{ans}</Typography>;
  }
  return <Typography variant="body1" color="textPrimary" sx={{ whiteSpace: 'pre-wrap', fontWeight: 600 }}>{String(ans)}</Typography>;
};

// --- Main Component ---
export const FormAnswersViewer: React.FC<FormAnswersViewerProps> = ({
  formTemplateJson,
  formAnswersJson,
  form,
  answers,
}) => {
  // Use new forms API if form exists and we have actual answers (non-empty array)
  // If form exists but answers are empty, fall back to legacy formAnswersJson if available
  const hasForm = !!form;
  const hasNewApiAnswers = Array.isArray(answers) && answers.length > 0;
  const hasLegacyAnswers = !!formTemplateJson || !!formAnswersJson;
  const useNewApi = hasForm && (hasNewApiAnswers || !hasLegacyAnswers);

  // Allow proceed if we have a template, even if answers are missing
  const hasTemplate = !!formTemplateJson || !!form;
  if (!formAnswersJson && !hasTemplate && !answers) {
    return null;
  }

  // Helper to resolve display value (labels for options, date formatting)
  const getDisplayValue = (value: any, question?: Question) => {
    if (value === undefined || value === null || value === '') return "-";

    // 1. Resolve Options (Select/Multiselect)
    if (question?.options && question.options.length > 0) {
      if (Array.isArray(value)) {
        return value.map(v => {
          const option = question.options?.find(opt => opt.value === v);
          return option?.label || v;
        });
      }
      const option = question.options.find(opt => opt.value === value);
      if (option?.label) return option.label;
    }

    // 2. Format Dates (ISO strings)
    // Regex matches YYYY-MM-DDTHH:mm:ss...
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        // Check if time is 00:00:00 - if so, show only date without time
        const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
        const formatString = isMidnight ? 'PPP' : 'PPP p';
        return format(date, formatString, { locale: es });
      } catch (e) {
        return value;
      }
    }

    return value;
  };

  // Helper to render a single answer value or an array of them
  const renderValue = (value: any, questionText?: string) => {
    if (Array.isArray(value)) {
      // If items are detected as images, use a grid layout.
      // Otherwise (PDFs, videos, or unknown types), use full width.
      const isAllImages = value.every(v => typeof v === 'string' && isImageUrl(v));
      
      // Check if all items are media (images or videos) for grid layout
      const isAllMedia = value.every(v => 
        typeof v === 'string' && (isImageUrl(v) || isVideoUrl(v))
      );

      // For multiselect labels (non-URLs), display as comma-separated text
      if (!isAllMedia && value.length > 0 && typeof value[0] === 'string' && !isLikelyUrl(value[0])) {
        return (
          <Typography variant="body1" color="textPrimary" sx={{ whiteSpace: 'pre-wrap', fontWeight: 600 }}>
            {value.join(', ')}
          </Typography>
        );
      }

      return (
        <Grid container spacing={2} mt={0.5}>
          {value.map((ans, index) => (
            <Grid
              item
              xs={12}
              sm={isAllMedia ? 6 : 12}
              md={isAllImages ? 4 : isAllMedia ? 6 : 12}
              key={index}
            >
              <RenderAnswerContent ans={ans} questionText={questionText} />
            </Grid>
          ))}
        </Grid>
      );
    }
    return <RenderAnswerContent ans={value} questionText={questionText} />;
  };

  let formDefinition: Form | null = null;
  if (useNewApi && form) {
    // Use the form object directly from new API
    formDefinition = form;
  } else if (formTemplateJson) {
    try {
      formDefinition = JSON.parse(formTemplateJson);
    } catch (e) {
      console.error('Error parsing form template JSON:', e);
      return <Typography color="error">Error al cargar la plantilla del formulario.</Typography>;
    }
  }

  // Process answers using shared utilities
  let processedAnswers: any;
  if (useNewApi && answers) {
    processedAnswers = processAnswers(answers);
  } else {
    try {
      processedAnswers = JSON.parse(formAnswersJson as string);
    } catch (e) {
      // If parsing fails, treat the whole string as a single answer.
      return renderValue(formAnswersJson);
    }
  }

  // If there's no form definition, but we have parsable answers (e.g., an array of URLs)
  if (!formDefinition) {
    return renderValue(processedAnswers);
  }

  // Separate regular answers from FOR_EACH answers
  let formAnswers: ProcessedFormAnswers | null = null;
  let forEachAnswers: any[] = [];

  if (Array.isArray(processedAnswers)) {
    const separated = separateAnswers(processedAnswers);
    formAnswers = separated.regularAnswers;
    forEachAnswers = separated.forEachAnswers;
  } else if (typeof processedAnswers === 'object' && processedAnswers !== null) {
    formAnswers = processedAnswers;
  }

  // Group FOR_EACH answers by their context
  const groupedForEachAnswers = groupForEachAnswers(forEachAnswers);

  // Helper function to render FOR_EACH answers in a structured way
  const renderForEachAnswers = () => {
    if (Object.keys(groupedForEachAnswers).length === 0) {
      return null;
    }

    return (
      <Box mt={2}>
        {Object.entries(groupedForEachAnswers).map(([contextKey, contextData]) => {
          const { contextValue, parentContextId, answers } = contextData;

          const firstAnswerQuestionId = Object.keys(answers)[0];
          const forEachQuestionDef = findQuestion(formDefinition, firstAnswerQuestionId);
          const sourceQuestionId = findSourceQuestionId(forEachQuestionDef, parentContextId, formDefinition);
          const contextLabel = getForEachContextLabel(contextValue, sourceQuestionId, formDefinition);

          return (
            <Box key={contextKey} mb={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#1976d2' }}>
                {contextLabel}
              </Typography>

              <Box ml={2}>
                <Grid container spacing={3}>
                  {Object.entries(answers).map(([questionId, value]) => {
                    const question = findQuestion(formDefinition, questionId);
                    if (!question || value === undefined || value === null || value === '') {
                      return null;
                    }

                    let questionText = question.text;
                    if (question.display_logic?.context_label_template) {
                      questionText = question.display_logic.context_label_template.replace(/\{label\}/g, contextLabel);
                    }

                    return (
                      <Grid item xs={12} sm={6} md={4} key={questionId}>
                        <Paper elevation={0} sx={{ p: 2, height: '100%', backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block', fontWeight: 500, textTransform: 'uppercase' }}>
                            {questionText}
                          </Typography >
                          <Box>
                            {renderValue(getDisplayValue(value, question), questionText)}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (!formAnswers || Object.keys(formAnswers).length === 0) {
    // Handle cases where answer is just a string, e.g. "https://some-url.com/image.png"
    if (typeof processedAnswers === 'string') {
      return renderValue(processedAnswers);
    }

    // If we only have FOR_EACH answers, render them
    if (forEachAnswers.length > 0) {
      return renderForEachAnswers();
    }

    return null;
  }

  // Identify source questions that trigger FOR_EACH logic to hide them from main list
  const sourceQuestionsToHide = getSourceQuestionsToHide(formDefinition);

  // Get filtered regular questions (non-FOR_EACH)
  const regularQuestions = filterRegularQuestions(formDefinition, sourceQuestionsToHide);

  return (
    <Box>
      {/* Render regular (non-FOR_EACH) questions */}
      <Grid container spacing={3}>
        {regularQuestions
          .map((question) => {
            // Use uuid for API answers, fallback to id for legacy compatibility
            const answerKey = question.uuid || question.id;
            const answer = formAnswers?.[answerKey];

            // Allow empty answers to proceed
            // if (answer === undefined || answer === null || answer === '') { ... }

            const displayValue = getDisplayValue(answer, question);

            return (
              <Grid item xs={12} sm={6} md={4} key={question.id}>
                <Box mb={2.5}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {question.text}
                  </Typography>
                  <Box mt={0.5}>
                    {renderValue(displayValue, question.text)}
                  </Box>
                </Box>
              </Grid>
            );
          })}
      </Grid>

      {/* Render FOR_EACH answers */}
      {renderForEachAnswers()}
    </Box>
  );
};

export default FormAnswersViewer; 